import { Component, type ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import OffersSlimComponent, { type OffersSlimIndexProps } from '@cx-modules/offers-slim';

import { OffersClickoutGuard } from '../offers-clickout';

// Shared OffersSlim wrapper (NGF-6504), single source of truth for RR7 atlas and atlas-astro. Renders
// the @cx-modules/offers-slim module with the app-agnostic pre-configuration RR7 used to apply inline:
// the OffersClickoutGuard, i18n injection, and an ErrorBoundary. Two things were host-decoupled in the
// extraction (same pattern as OffersList, NGF-6397):
//   - The error boundary takes an `onError` callback instead of importing @sentry/react-router (which
//     @cx-web/components cannot depend on). RR7 injects `captureException`; atlas-astro (no Sentry yet)
//     injects a console.error shim so failures still surface in the server log / browser console.
//   - `analyticsDataLayer` (window.dataLayer in RR7) is not injected here: @cx-modules/offers-slim
//     already defaults this prop to `window.dataLayer` on the client internally, so reading the window
//     global in this shared package would be redundant (and unsafe during SSR). Callers may still pass
//     it explicitly via props if they need to override it.

export type OffersSlimProps = Omit<OffersSlimIndexProps, 'i18n'> & {
  /**
   * Called when the OffersSlim subtree throws (including the OffersClickoutGuard's deliberate throw on
   * missing/malformed clickout props). RR7 atlas wires this to Sentry's captureException; atlas-astro
   * (no Sentry yet) wires a console.error shim so the failure still surfaces in the log/console.
   */
  onError?: (error: unknown) => void;
};

class OffersSlimErrorBoundary extends Component<
  { children: ReactNode; onError?: (error: unknown) => void },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    this.props.onError?.(error);
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

export const OffersSlim = ({ onError, ...props }: OffersSlimProps) => {
  const { i18n } = useTranslation();

  const propsWithI18n = { ...props, i18n };

  return (
    <OffersSlimErrorBoundary onError={onError}>
      <Suspense fallback={null}>
        <OffersClickoutGuard componentName="OffersSlim" values={propsWithI18n}>
          <OffersSlimComponent {...propsWithI18n} />
        </OffersClickoutGuard>
      </Suspense>
    </OffersSlimErrorBoundary>
  );
};

type OffersSlimContentContractProps = {
  [key: string]: unknown;
  autoHideTerms?: boolean;
  geoTargeting?: boolean | string;
  ocApiKey: string;
  repub: string;
  subdivision?: string;
};

export const OffersSlimContentContractForPageTemplate = ({
  autoHideTerms,
  geoTargeting,
  ocApiKey,
  repub,
  subdivision,
  ...props
}: OffersSlimContentContractProps) => {
  return {
    ...props,
    // NGF-6300: apiKey is the pre-resolved per-repub OC key threaded via ComponentLoaderContext.ocApiKey
    // (no ambient global ENV / per-repub cast here — Astro-island safe).
    apiKey: ocApiKey,
    // Per-branch `as const` so the ternary infers the module's `OffersSlimVariant`
    // ('default' | 'autohide') instead of widening to `string` — without it, a consumer that types
    // this contract's output against the module's props (atlas-astro's
    // `defineDataIsland<OffersSlimIslandProps, …>`, NGF-6582) fails to compile even though the
    // values are correct. (`as const` can't wrap the ternary itself — TS1355.)
    variant: autoHideTerms ? ('autohide' as const) : ('default' as const),
    subdivisionCode: subdivision,
    repub,
    geoTargeting,
    inheritFontFamily: true,
    ...(geoTargeting === false
      ? {
          countryCode: undefined,
          subdivisionCode: undefined,
        }
      : {}),
  };
};
